// Compact temperature chip for a Live Map vehicle row. Reads the shared overview — no fetch here.
import { useTranslation } from 'react-i18next';
import { useColdChainShared } from './ColdChainShared';
import { summarizeColdPoints, statusTone, formatTemp } from './coldChainModel';

const TONE = {
  green: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  amber: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  red: 'bg-red-500/15 text-red-600 dark:text-red-400',
  grey: 'bg-slate-500/15 text-slate-500 dark:text-slate-400',
};

/** The row's cold summary, or null (module off / no enabled cold points). `deviceIds`: every tracker of the vehicle. */
function useColdSummary(deviceIds) {
  const shared = useColdChainShared();
  if (!shared) return null;
  return summarizeColdPoints(deviceIds.flatMap((id) => shared.byDevice.get(id) ?? []));
}

export default function ColdTempChip({ deviceIds, size = 'md', className = '' }) {
  const { t } = useTranslation();
  const summary = useColdSummary(deviceIds);
  if (!summary) return null;
  const tone = statusTone(summary.status);
  // FSI/PDI isolates do for a plain title what <bdi> does in markup.
  const tip = summary.points
    .map((p) => `\u2068${p.name}\u2069: ${p.status === 'no_data' ? t('coldChain.status.noData', 'No data') : formatTemp(p.value)}`)
    .join('\n');
  return (
    <span
      data-testid="cold-temp-chip"
      data-status={summary.status}
      title={`${t('coldChain.card.title', 'Cold chain')}\n${tip}`}
      className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded font-bold tabular-nums ${size === 'sm' ? 'px-1 py-px text-[9px]' : 'px-1.5 py-0.5 text-[10px]'} ${TONE[tone]} ${className}`}
    >
      <bdi>{summary.value === null ? '—' : formatTemp(summary.value)}</bdi>
    </span>
  );
}

/**
 * Wraps a row's existing content ONLY when the row shows a chip; otherwise returns `children`
 * untouched so rows without cold points (and orgs without the module) keep their original markup.
 * `inline`: chip beside the content in a flex span (compact name cell); else chip and content share one
 * right-aligned group (the comfortable row is `justify-between`, so they must be ONE flex child).
 */
export function ColdChipWrap({ deviceIds, inline = false, size, children }) {
  const summary = useColdSummary(deviceIds);
  if (!summary) return children;
  if (inline) {
    return (
      <span className="flex items-center gap-1 min-w-0 w-full">
        <span className="truncate min-w-0 flex-1">{children}</span>
        <ColdTempChip size={size} deviceIds={deviceIds} />
      </span>
    );
  }
  return (
    <span className="ms-2 flex flex-shrink-0 items-center gap-1.5">
      <ColdTempChip size={size} deviceIds={deviceIds} />
      {children}
    </span>
  );
}
